import React, { useState } from "react";
import AvatarEditor from "react-avatar-editor";
import Dropzone from "react-dropzone";
import {
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { classNames } from "@utilscommon";
import LocaleStrings from "@language";

const defaultScale = 1.2;
const scaleStep = 0.05;
const minScale = 1;
const maxScale = 2;

export default function ImageCropper(props) {
  let {
    displaySize,
    cropperSize,
    requiredSize,
    border,
    baseClassName = "UPLOAD_IMAGE_CROPPER",
    displayText,
    fileSizeText,
  } = props;
  const [canvasRecource, setCanvasRecource] = useState(props.canvasRecource);
  const [imageUrl, setImagepath] = useState(props.imagepath);
  const [scale, setScale] = useState(defaultScale);
  const [imageType, setImageType] = useState("");
  const [imageErrorMsg, setImageErrorMsg] = useState("");
  let editor = "";

  const setEditorRef = (edit) => (editor = edit);

  const onClickSave = () => {
    saveImage(null);
  };

  const handleImageCapture = () => {
    saveImage("capture");
  };

  const saveImage = (callback) => {
    if (editor) {
      let img;
      if (imageType === "image/png") img = editor.getImage().toDataURL();
      else img = editor.getImage().toDataURL("image/jpeg", 0.9);
      // console.log("img :- ", img);

      if (callback) {
        props.onImageSave(img);
      } else {
        props.onImageSave(img);
        setImagepath(img);
      }
    }
  };

  const onDropFile = (files) => {
    // console.log("files :- ", files);

    let file = files[0];
    let requiredWidth = requiredSize.width;
    let requiredHeight = requiredSize.height;
    let reader = new FileReader();
    if (file && file.type.indexOf("image") >= 0) {
      reader.onload = (e) => {
        let img = new Image();
        img.src = reader.result;
        img.onload = function (obj) {
          let width = this.width;
          let height = this.height;

          if (width < requiredWidth || height < requiredHeight) {
            setImageErrorMsg(LocaleStrings.kInvalidImageSizeText);
          } else {
            setImageType(file.type);
            setImageErrorMsg("");
            setCanvasRecource(reader.result);
          }
        };
      };
      reader.readAsDataURL(file);
    }
  };

  const resetImage = () => {
    setScale(defaultScale);
    setCanvasRecource("");
    setImagepath("");
    setImageErrorMsg("");
    setImageType("");

    props.onImageSave("");
  };

  const onZoomOut = () => {
    var scale1 = scale;
    scale1 = scale1 - scaleStep;
    if (scale1 < minScale) scale1 = minScale;
    setScale(scale1);

    handleImageCapture();
  };

  const onZoomIn = () => {
    var scale1 = scale;
    scale1 = scale1 + scaleStep;
    if (scale1 > maxScale) scale1 = maxScale;
    setScale(scale1);

    handleImageCapture();
  };

  var hideDropZoneClass = ""; // Used for dropzone
  var hideCropZoneClass = "hidden"; // Used for avatar editor
  var hideControlButton = "hidden"; // Used for image or error message remove button
  var imageDisplayContainer = {
    width: displaySize.width,
    height: displaySize.height,
  };

  if (imageUrl && imageUrl !== "file object not found") {
    hideDropZoneClass = "hidden";
    hideCropZoneClass = "hidden";
    hideControlButton = "";
  }
  if (canvasRecource && !imageUrl) {
    hideDropZoneClass = "hidden";
    hideCropZoneClass = "";
  }
  if (imageErrorMsg !== "") {
    hideDropZoneClass = "hidden";
    hideControlButton = "";
  }

  // Display file size message and hide while cropping
  let filesizemessage = "";
  if (fileSizeText && fileSizeText !== "" && hideCropZoneClass === "hidden") {
    let mapObj = {
      "#width": requiredSize.width,
      "#height": requiredSize.height,
    };

    filesizemessage = fileSizeText.replace(
      /#width|#height/gi,
      function (matched) {
        return mapObj[matched];
      }
    );
  }

  return (
    <>
      <div
        style={imageDisplayContainer}
        className={`${baseClassName} PROFILE_IMAGE image-section ${hideDropZoneClass}`}
      >
        <Dropzone
          onDrop={onDropFile}
          accept={{
            "image/jpeg": [],
            "image/jpg": [],
            "image/png": [],
          }}
          multiple={false}
        >
          {({ getRootProps, getInputProps }) => (
            <div
              {...getRootProps()}
              className={`${baseClassName}_DROPZONE_SECTION PROFILE_IMAGE_DROPZONE_SECTION flex items-center w-full h-full cursor-pointer px-6 pt-5 pb-6 border-2 border-default border-dashed rounded-md`}
            >
              <input {...getInputProps()} />
              <div
                className={classNames(
                  `${baseClassName}_DROPZONE_CONTENT PROFILE_IMAGE_DROPZONE_CONTENT space-y-1 text-center`
                )}
              >
                <svg
                  className={classNames(
                    `${baseClassName}_DROPZONE_CONTENT_ICON PROFILE_IMAGE_DROPZONE_CONTENT_ICON mx-auto h-12 w-12 text-gray-400`
                  )}
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                  aria-hidden="true"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div
                  className={classNames(
                    `${baseClassName}_DROPZONE_CONTENT_TEXT PROFILE_IMAGE_DROPZONE_CONTENT_TEXT custom-text-primary-sm-normal`
                  )}
                >
                  {displayText}
                </div>
              </div>
            </div>
          )}
        </Dropzone>
      </div>

      {imageErrorMsg && imageErrorMsg !== "" ? (
        <div
          className={classNames(
            `${baseClassName}_ERROR_MESSAGE_SECTION PROFILE_IMAGE_ERROR_MESSAGE_SECTION image-alert-message-section`
          )}
        >
          <div
            className={classNames(
              `${baseClassName}_ERROR_MESSAGE_TEXT PROFILE_IMAGE_ERROR_MESSAGE_TEXT alert-message custom-text-primary-md-medium`
            )}
          >
            {imageErrorMsg}
          </div>

          <button
            type="button"
            className={classNames(
              `${baseClassName}_ERROR_MESSAGE_RESET_BUTTON PROFILE_IMAGE_ERROR_MESSAGE_RESET_BUTTON danger-icon-button-circle-xs reset-button`
            )}
            onClick={resetImage}
          >
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      ) : (
        <>
          <div
            className={classNames(
              `${baseClassName}_CROPPED_IMAGE_SECTION PROFILE_IMAGE_CROPPED_IMAGE_SECTION image-section image-display ${hideControlButton}`
            )}
            style={imageDisplayContainer}
          >
            <img
              src={imageUrl}
              alt=""
              className={classNames(
                `${baseClassName}_CROPPED_IMAGE PROFILE_IMAGE_CROPPED_IMAGE h-full`
              )}
            />
            <div className={`cropper-remove-button-position`}>
              <button
                type="button"
                className={classNames(
                  `${baseClassName}_CROPPED_IMAGE_REMOVE_BUTTON PROFILE_IMAGE_CROPPED_IMAGE_REMOVE_BUTTON danger-icon-button-circle-xs`
                )}
                onClick={resetImage}
              >
                <XMarkIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div
            className={classNames(
              `${baseClassName}_AVATAR_EDITOR_SECTION PROFILE_IMAGE_AVATAR_EDITOR_SECTION space-y-2 ${hideCropZoneClass}`
            )}
          >
            <AvatarEditor
              image={canvasRecource}
              color={[0, 0, 0, 0.5]} // RGBA
              scale={scale}
              rotate={0}
              ref={setEditorRef}
              disabledrop="true"
              border={border ? border : 50}
              width={cropperSize.width}
              height={cropperSize.height}
              className={classNames(
                `${baseClassName}_AVATAR_EDITOR_CANVAS PROFILE_IMAGE_AVATAR_EDITOR_CANVAS editor-canvas`
              )}
              style={{ background: "#777" }}
              onImageReady={handleImageCapture}
              onPositionChange={handleImageCapture}
            />
            <div
              className={classNames(
                `${baseClassName}_ACTIONS_BUTTONS_SECTIONS PROFILE_IMAGE_ACTIONS_BUTTONS_SECTIONS space-x-2 flex item-center`
              )}
            >
              <button
                type="button"
                className={classNames(
                  `${baseClassName}_ACTION_ZOOMIN_BUTTON PROFILE_IMAGE_ACTION_ZOOMIN_BUTTON default-icon-button-xs`
                )}
                onClick={onZoomIn}
              >
                <MagnifyingGlassPlusIcon
                  className="h-4 w-5"
                  aria-hidden="true"
                />
              </button>
              <button
                type="button"
                className={classNames(
                  `${baseClassName}_ACTION_ZOOMOUT_BUTTON PROFILE_IMAGE_ACTION_ZOOMOUT_BUTTON default-icon-button-xs`
                )}
                onClick={onZoomOut}
              >
                <MagnifyingGlassMinusIcon
                  className="h-4 w-5"
                  aria-hidden="true"
                />
              </button>
              <button
                type="button"
                className={classNames(
                  `${baseClassName}_ACTION_CROP_BUTTON PROFILE_IMAGE_ACTION_CROP_BUTTON theme-button-sm`
                )}
                onClick={onClickSave}
              >
                {LocaleStrings.kCropText}
              </button>
              <button
                type="button"
                className={classNames(
                  `${baseClassName}_ACTION_REMOVE_BUTTON PROFILE_IMAGE_ACTION_REMOVE_BUTTON danger-icon-button-xs`
                )}
                onClick={resetImage}
              >
                <XMarkIcon className="h-4 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </>
      )}

      {filesizemessage !== "" ? (
        <div
          className={classNames(
            `${baseClassName}_MIN_SIZE_TEXT PROFILE_IMAGE_MIN_SIZE_TEXT custom-text-secondary-sm-normal`
          )}
        >
          {filesizemessage}
        </div>
      ) : (
        ""
      )}
    </>
  );
}
